/**
 * tracking-drawer.js
 */

import React from 'react';
import { 
  Drawer,
  Box,
} from '@mui/material';
import { useHistory } from 'react-router-dom';
import analytics from 'utils/analytics';
import { UserContext } from 'contexts/user/user-context';
import FeatureBox from 'components/feature-box';
import ConfirmDialog from 'components/confirm-dialog';
import { TRACKING_STATE, TRACK_COMPLETION_ACTION } from '../tracking-common';
import { MapIconButton } from '../map/map-controls';
import { LiveTrackingContext } from './live-tracking-context';
import { TrackingFormContext } from './tracking-form-context';
import Tracking from './tracking';


export default function TrackingDrawer(props) {
  const history = useHistory();
  const { user } = React.useContext(UserContext);
  const { 
    trackingState, 
    stopTracking, 
    resetTracking,
    completeActiveTrack, 
    discardActiveTrack, 
    isTrackingOpen,
    setIsTrackingOpen,
  } = React.useContext(LiveTrackingContext);
  const { trackType } = React.useContext(TrackingFormContext);
  const [confirmCloseAction, setConfirmCloseAction] = React.useState();
  const [isLoading, setIsLoading] = React.useState(false);
  const [isContentMounted, setIsContentMounted] = React.useState(isTrackingOpen || trackingState !== TRACKING_STATE.UNSTARTED);
    

  // ensure contents are always mounted when drawer is opened
  React.useEffect(() => {
    if (isTrackingOpen) {
      setIsContentMounted(true);
    }
  }, [isTrackingOpen]);


  const onClose = async () => {
    if (user) {
      setIsTrackingOpen(false);

      if (trackingState === TRACKING_STATE.UNSTARTED) {
        // Unmount unneeded subcomponents after a delay, allowing the drawer to close smoothly first
        setTimeout(() => setIsContentMounted(false), 1000);
      } else {
        // We're leaving things running. Log for analytics.
        analytics.trackGA4('event', { name: 'track_exit', params: { trackexit_track_type: trackType } });
      }
    } else {
      // anonymous layer: check if still tracking and give options
      if (trackingState !== TRACKING_STATE.UNSTARTED) {
        const response = await confirmCloseAction();
        if (response === 'save') {
          setIsLoading(true);
          stopTracking();
          await completeActiveTrack();
          resetTracking(TRACK_COMPLETION_ACTION.SAVE);
          setIsLoading(false);
        } else if (response === 'discard') {
          setIsLoading(true);
          stopTracking();
          await discardActiveTrack();
          resetTracking(TRACK_COMPLETION_ACTION.DISCARD);
          setIsLoading(false);
        } else if (response === 'cancel') {
          return;
        }
      }
      
      history.push('/');
    }
  }
  

  const closeDrawerButton = (
    <MapIconButton
      typeName='mobile-close-drawer'
      onClick={onClose}
      sxProps={{
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 201,
      }}
    />
  );


  return (
    <>
      <Drawer
        anchor='bottom'
        variant={'persistent'}
        sx={{ textAlign: 'left', '& .MuiDrawer-paper': { borderWidth: 0 } }}
        open={isTrackingOpen}
        onClose={onClose}
      >
        {isContentMounted &&
        <FeatureBox loading={isLoading}>
          <Box sx={{ height: '100vh', overflow: 'hidden' }}>
            <Box sx={{ height: '100vh', zIndex: 0, position: 'relative' }}>
              <Tracking />
            </Box>
            {closeDrawerButton}
          </Box>
        </FeatureBox>}
      </Drawer>

      {/* anonymous mid-tracking drawer-close options modal */}
      <ConfirmDialog
        title='Tracking Is Active'
        content={'You have attempted to close while tracking is still active.'}
        titleStyle='warning'
        titleIcon='warning'
        setConfirm={setConfirmCloseAction}
        customActions={[{
          label: 'Cancel Close',
          value: 'cancel',
        }, {
          label: 'Discard Track',
          value: 'discard'
        }, {
          label: 'Save Track',
          value: 'save'
        }]}
      />
    </>
  );
}